import { useEffect, useState } from "react";

/**
 * Progressive Web App support: the service worker (public/sw.js) and the
 * "Install app" prompt.
 *
 * Chrome / Edge / Android fire `beforeinstallprompt` once, early — often before
 * React mounts — so it's captured here at import time and handed to the hook.
 * iOS Safari has no prompt: the hook reports `isIOS` so the UI can explain
 * Share → Add to Home Screen instead.
 */

type InstallEvent = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> };

let deferred: InstallEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault(); // keep it for our own menu item instead of the mini-infobar
    deferred = e as InstallEvent;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferred = null;
    notify();
  });
}

export const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia?.("(display-mode: standalone)").matches || (window.navigator as any).standalone === true);

const isIOSDevice = () =>
  typeof navigator !== "undefined" &&
  (/iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1));

export const usePwaInstall = () => {
  const [, force] = useState(0);
  useEffect(() => {
    const l = () => force((n) => n + 1);
    listeners.add(l);
    return () => {
      listeners.delete(l);
    };
  }, []);
  const standalone = isStandalone();
  return {
    /** The browser offered an install prompt we can show. */
    canInstall: !standalone && Boolean(deferred),
    /** iPhone / iPad in the browser: install is manual (Share → Add to Home Screen). */
    showIOSHint: !standalone && isIOSDevice(),
    installed: standalone,
    promptInstall: async () => {
      if (!deferred) return false;
      const e = deferred;
      deferred = null;
      notify();
      await e.prompt();
      const choice = await e.userChoice;
      return choice.outcome === "accepted";
    },
  };
};

/**
 * Register public/sw.js in production builds. A new version activates on the
 * next load (the worker serves pages network-first, so users never get stuck
 * on an old build).
 */
export const registerServiceWorker = () => {
  if (process.env.NODE_ENV !== "production" || !("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register(`${process.env.PUBLIC_URL || ""}/sw.js`)
      .then((reg) => {
        // Look for a new version whenever the app comes back to the foreground.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") reg.update().catch(() => undefined);
        });
      })
      .catch((err) => console.warn("Service worker registration failed:", err));
  });
};

/** Online / offline, live. */
export const useOnline = () => {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
};
